import { formatDateTime } from '@takumi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Workflow } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/form';
import { Badge, Card, CardHeader, EmptyState, PageLoader } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useUserOptions } from '@/lib/queries';
import { cn } from '@/lib/utils';

interface Action {
  type: 'create_task' | 'notify' | 'assign_owner' | 'send_whatsapp_template';
  title?: string;
  userId?: string | null;
  templateKey?: string;
  dueInHours?: number;
  to?: 'owner' | 'team';
}
interface Rule {
  id: string;
  key: string | null;
  name: string;
  description: string | null;
  trigger: string;
  triggerLabel: string;
  delayHours: number;
  isActive: boolean;
  actions: Action[];
  runs: Record<string, number>;
}
interface Run {
  id: string;
  status: 'scheduled' | 'done' | 'skipped' | 'failed';
  runAt: string;
  finishedAt: string | null;
  log: string[];
  organizationId: string | null;
  ruleName: string;
  organizationName: string | null;
}

const ACTION_LABEL: Record<Action['type'], (a: Action) => string> = {
  create_task: (a) => `משימה: ${a.title}`,
  notify: (a) => `התראה ל${a.to === 'team' ? 'צוות' : 'אחראי'}: ${a.title}`,
  assign_owner: () => 'שיוך לאחראי',
  send_whatsapp_template: (a) => `WhatsApp — תבנית ${a.templateKey}`,
};
const STATUS: Record<Run['status'], { label: string; v: 'neutral' | 'matcha' | 'today' | 'overdue' }> = {
  scheduled: { label: 'ממתין', v: 'neutral' },
  done: { label: 'בוצע', v: 'matcha' },
  skipped: { label: 'דולג', v: 'today' },
  failed: { label: 'נכשל', v: 'overdue' },
};

function delayText(h: number) {
  if (!h) return 'מיד';
  if (h % 24 === 0) return `אחרי ${h / 24} ימים`;
  return `אחרי ${h} שעות`;
}

/** Trigger → wait → conditions → actions. Rules are seeded from the brief; each can be switched on/off and tuned. */
export function AutomationsSettings() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const users = useUserOptions();
  const rules = useQuery({ queryKey: ['automationRules'], queryFn: () => api.get<Rule[]>('/automations/rules') });
  const runs = useQuery({ queryKey: ['automationRuns'], queryFn: () => api.get<Run[]>('/automations/runs'), refetchInterval: 60_000 });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['automationRules'] });
    void qc.invalidateQueries({ queryKey: ['automationRuns'] });
  };
  const update = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => api.patch(`/automations/rules/${v.id}`, v.body),
    onSuccess: () => {
      refresh();
      toast.success('הכלל עודכן');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const process = useMutation({
    mutationFn: () => api.post<{ done: number; skipped: number; failed: number }>('/automations/process'),
    onSuccess: (r) => {
      refresh();
      toast.success(`בוצעו ${r.done}, דולגו ${r.skipped}${r.failed ? `, נכשלו ${r.failed}` : ''}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const editable = can('settings.manage');
  if (!rules.data) return <PageLoader />;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title="אוטומציות"
          icon={<Workflow />}
          actions={editable && <Button size="sm" variant="secondary" onClick={() => process.mutate()} loading={process.isPending}><Play /> הרצת ממתינים עכשיו</Button>}
        />
        <p className="px-4 pt-3 text-sm text-muted">כל כלל: אירוע ← המתנה ← בדיקת תנאים (לפי המצב העדכני) ← פעולות. כל ביצוע נרשם ביומן ובציר הזמן של הלקוח.</p>
        <ul className="divide-y divide-line/70">
          {rules.data.map((r) => <RuleRow key={r.id} rule={r} editable={editable} users={users.data ?? []} onSave={(body) => update.mutate({ id: r.id, body })} />)}
        </ul>
      </Card>
      <Card>
        <CardHeader title="יומן ביצועים" icon={<Workflow />} count={runs.data?.length} />
        {!runs.data?.length ? (
          <EmptyState title="עדיין אין ביצועים" />
        ) : (
          <ul className="divide-y divide-line/70 text-sm">
            {runs.data.map((run) => (
              <li key={run.id} className="flex flex-wrap items-start gap-2 px-4 py-2.5">
                <Badge variant={STATUS[run.status].v}>{STATUS[run.status].label}</Badge>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">
                    {run.ruleName}
                    {run.organizationId && <> · <Link className="text-matcha-700 hover:underline" to={`/org/${run.organizationId}`}>{run.organizationName}</Link></>}
                  </div>
                  {run.log.length > 0 && <div className="text-xs text-muted">{run.log.join(' · ')}</div>}
                </div>
                <span className="text-xs text-muted tabular">{formatDateTime(run.finishedAt ?? run.runAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function RuleRow({ rule, editable, users, onSave }: { rule: Rule; editable: boolean; users: { id: string; fullName: string }[]; onSave: (b: Record<string, unknown>) => void }) {
  const [delayDays, setDelayDays] = useState(String(Math.round((rule.delayHours / 24) * 10) / 10));
  const assign = rule.actions.find((a) => a.type === 'assign_owner');
  return (
    <li className={cn('flex flex-wrap items-start gap-3 px-4 py-3', !rule.isActive && 'opacity-70')}>
      <label className="mt-1 inline-flex cursor-pointer items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={rule.isActive}
          disabled={!editable}
          aria-label={`הפעלת ${rule.name}`}
          onChange={(e) => onSave({ isActive: e.target.checked })}
        />
        <span className="relative h-5 w-9 rounded-full bg-line-strong transition peer-checked:bg-matcha-600 after:absolute after:top-0.5 after:start-0.5 after:size-4 after:rounded-full after:bg-white after:transition peer-checked:after:translate-x-[-1rem]" />
      </label>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{rule.name}</div>
        {rule.description && <div className="text-sm text-ink-soft">{rule.description}</div>}
        <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
          <Badge variant="outline">{rule.triggerLabel}</Badge>
          <Badge variant="outline">{delayText(rule.delayHours)}</Badge>
          {rule.actions.map((a, i) => <Badge key={i} variant="matcha">{ACTION_LABEL[a.type](a)}</Badge>)}
        </div>
        {editable && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted">המתנה (ימים):</span>
            <Input type="number" min={0} max={90} step="0.5" className="h-8 w-20" value={delayDays} onChange={(e) => setDelayDays(e.target.value)} aria-label="ימי המתנה" />
            <Button size="sm" variant="ghost" onClick={() => onSave({ delayHours: Math.round(Number(delayDays) * 24) })} disabled={Math.round(Number(delayDays) * 24) === rule.delayHours}>שמירה</Button>
            {assign && (
              <>
                <span className="text-muted">אחראי:</span>
                <Select
                  className="h-8 w-44"
                  aria-label="אחראי לשיוך"
                  value={assign.userId ?? ''}
                  onChange={(e) => onSave({ actions: rule.actions.map((a) => (a.type === 'assign_owner' ? { type: 'assign_owner', userId: e.target.value || null } : a)) })}
                >
                  <option value="">לא נבחר</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                </Select>
              </>
            )}
          </div>
        )}
      </div>
      <div className="text-xs text-muted tabular">
        {rule.runs.done ?? 0} בוצעו · {rule.runs.scheduled ?? 0} ממתינים
      </div>
    </li>
  );
}
